/**
 * O painel do eixo Trilha com a jornada consolidada (E84.F4) — "Eventos e horários" desde a
 * E156.F3 (ADR-0106 §2.8).
 *
 * Módulo próprio porque `QuadroPorEixosPaineis` já passa do teto de 200 linhas
 * do handbook §8 — e porque a jornada importa `FatosDoEixo` de lá: deixar o
 * painel lá dentro fecharia um ciclo de import (o mesmo motivo do vínculo na
 * E84.F3). Mesma regra dos demais painéis: a frase do que o eixo confronta e
 * APENAS o que o backend já devolveu — a lista ordenada de instantes com o
 * rótulo, o instante, o selo da fonte e os selos que o motor levantou (fora
 * de ordem, UTC/local, fuso presumido, lido em mês/dia), e os quatro itens da
 * cronologia. Exame anterior ao estágio (`jornada` nula) mostra a linha do
 * tempo dos eventos como antes, mais a nota de legado.
 */
import { Badge, MonoData } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { InstanteDaJornadaData, JornadaApuradaData } from '@/lib/exame_eletronico';
import type { RegistroDoInstanteData } from '@/lib/exame_eletronico_jornada';
import { nomeDoInstante, origemDoRegistro, rotuloDaFonte } from '@/lib/exame_eletronico_jornada';

import { ComoLer, FatosDoEixo, type PainelDoEixoProps } from './QuadroPorEixosPaineis';
import { RegistrosDaJornada } from './QuadroPorEixosRegistros';
import { formatarInstante, LinhaDoTempo, Titulo } from './QuadroVerificacaoTabelas';

const t = ptBR.exameEletronico.eixos;
const tq = ptBR.exameEletronico.quadro;
const tj = tq.jornada;

const VINCULO_VARIANT = {
  identificador: 'info',
  indicio: 'warning',
  sem_identificador: 'neutral',
} as const;

/** E156.F3 — o que o registro do banco acrescenta ao instante: o vínculo, o evento como consta e a origem. */
function SelosDoRegistro({ registro }: { registro: RegistroDoInstanteData }): JSX.Element {
  const equivalente = registro.equivalente_a === 'protocolo';
  return (
    <>
      <Badge
        variant={equivalente ? 'success' : VINCULO_VARIANT[registro.vinculo]}
        data-vinculo={equivalente ? 'equivalente' : registro.vinculo}
      >
        {equivalente ? tj.vinculo.equivalente : tj.vinculo[registro.vinculo]}
      </Badge>
      <span className="text-fg-muted">{registro.evento_literal}</span>
      <MonoData variant="muted">{origemDoRegistro(registro.origem)}</MonoData>
    </>
  );
}

function Selos({ instante }: { instante: InstanteDaJornadaData }): JSX.Element {
  return (
    <>
      <Badge variant="neutral" data-fonte={instante.fonte}>
        {rotuloDaFonte(instante.fonte)}
      </Badge>
      {instante.registro ? <SelosDoRegistro registro={instante.registro} /> : null}
      {instante.lido_em_mes_dia ? <Badge variant="warning">{tj.lidoEmMesDia}</Badge> : null}
      {instante.fuso_presumido ? <Badge variant="neutral">{tj.fusoPresumido}</Badge> : null}
      {instante.fora_de_ordem ? <Badge variant="danger">{tq.foraDeOrdem}</Badge> : null}
      {instante.utc_local_incoerentes ? (
        <Badge variant="warning">{tq.utcLocalIncoerentes}</Badge>
      ) : null}
    </>
  );
}

function ListaDeInstantes({ instantes }: { instantes: readonly InstanteDaJornadaData[] }) {
  if (instantes.length === 0) {
    return <p className="text-body-sm text-fg-dim">{tj.semInstantes}</p>;
  }
  return (
    <ol className="space-y-sc-1">
      {instantes.map((instante, indice) => (
        <li
          key={`${instante.fonte}-${instante.rotulo}-${indice}`}
          className="flex flex-wrap items-center gap-sc-2 border-l-2 border-border pl-sc-3 text-body-sm"
          data-testid={`quadro-instante-${indice}`}
          data-fonte={instante.fonte}
        >
          <MonoData as="time" variant="muted" {...(instante.em ? { dateTime: instante.em } : {})}>
            {formatarInstante(instante.em, true)} UTC
          </MonoData>
          <span className="font-medium text-fg">{nomeDoInstante(instante)}</span>
          <Selos instante={instante} />
        </li>
      ))}
    </ol>
  );
}

/**
 * A jornada consolidada: a lista posicionada com a fonte ao lado, o que o registro do banco
 * acrescenta (E156.F3), os quatro itens e o limite no rodapé. O instante sem posição (sem fuso
 * declarado) sai à parte, com o literal; os ticks têm a própria lista.
 */
export function JornadaConsolidada({
  jornada,
  sequenciaIlogica,
}: {
  jornada: JornadaApuradaData;
  sequenciaIlogica: boolean;
}): JSX.Element {
  const posicionados = jornada.instantes.filter((i) => Boolean(i.em));
  const semFuso = jornada.instantes.filter((i) => !i.em && i.rotulo !== 'ticks_do_nome_do_arquivo');
  return (
    <section data-testid="quadro-jornada" className="space-y-sc-3">
      <Titulo>{tj.titulo}</Titulo>
      <p className="max-w-prose text-body-sm text-fg-muted">{tj.subtitulo}</p>
      {sequenciaIlogica ? (
        <p
          className="border border-danger bg-danger-50 px-sc-3 py-sc-2 text-body-sm text-danger-700"
          role="alert"
          data-testid="quadro-sequencia-ilogica"
        >
          {tq.sequenciaIlogica}
        </p>
      ) : null}
      <ListaDeInstantes instantes={posicionados} />
      <RegistrosDaJornada jornada={jornada} semFuso={semFuso} />
      <FatosDoEixo itens={jornada.itens} testId="quadro-cronologia" />
      <p className="max-w-prose text-caption text-fg-dim">{tj.declaratoria}</p>
      <p className="max-w-prose text-caption text-fg-dim" data-testid="quadro-jornada-limite">
        {tj.limite}
      </p>
    </section>
  );
}

export function PainelTrilha({ resultado }: PainelDoEixoProps): JSX.Element {
  const jornada = resultado.jornada ?? null;
  return (
    <div className="space-y-sc-4" data-testid="eixo-painel-trilha">
      <ComoLer texto={t.confronta.trilha} testId="como-ler-trilha" />
      {jornada === null ? (
        <>
          <LinhaDoTempo
            eventos={resultado.linha_do_tempo}
            sequenciaIlogica={resultado.sequencia_ilogica}
          />
          <p className="text-body-sm text-fg-dim" data-testid="quadro-jornada-nao-examinada">
            {tj.naoExaminada}
          </p>
        </>
      ) : (
        <JornadaConsolidada jornada={jornada} sequenciaIlogica={resultado.sequencia_ilogica} />
      )}
    </div>
  );
}
